import { defineCollection } from "@rebasepro/cms-types";
import type { CollectionConfig } from "@rebasepro/types";
import gamesCollection from "./games";
import playersCollection from "./players";
import { moment } from "../words";

// Who played which party game, and how they did: each phone sends its own result when a game ends (POST /games/me,
// signed in as its account or guest), and backend/src/games.ts ties it to its game. Only games played since this was
// added have them; older games keep just their player count.
const one = (target: () => CollectionConfig, relationName: string, from: string, table: string) => ({
    type: "relation" as const,
    admin: { readOnly: true },
    relation: { kind: "via" as const, cardinality: "one" as const, target, relationName, joinPath: [{ table, on: { from, to: "id" } }] },
});

const gamePlayersCollection = defineCollection({
    name: "Game results",
    singularName: "Game result",
    slug: "game_players",
    table: "songbuzz_game_players",
    description: "Each player's result in each party game: name, place, score. Sent by the phones since 3 Nov 2026.",
    securityRules: [
        { operation: "select", roles: ["admin"] },
        { operation: "delete", roles: ["admin"] },
    ],
    properties: {
        id: { name: "Result", type: "number", isId: true, columnType: "bigserial", admin: { readOnly: true } },
        name: { name: "Name in the game", type: "string", admin: { readOnly: true } },
        pos: { name: "Place", type: "number", columnType: "integer", admin: { readOnly: true } },
        ofPlayers: { name: "Players in the game", type: "number", columnType: "integer", admin: { readOnly: true } },
        score: { name: "Score", type: "number", columnType: "integer", admin: { readOnly: true } },
        rightAnswers: { name: "Right answers", type: "number", columnType: "integer", admin: { readOnly: true } },
        player: { name: "Player id", type: "string", validation: { required: true }, admin: { readOnly: true, hideFromCollection: true } },
        who: { name: "Player", ...one((): CollectionConfig => playersCollection, "who", "player", "songbuzz_players") },
        gameId: { name: "Game id", type: "number", columnType: "bigint", admin: { readOnly: true, hideFromCollection: true } },
        game: { name: "Game", ...one((): CollectionConfig => gamesCollection, "game", "game_id", "songbuzz_games") },
        gameKey: { name: "Game key", type: "string", validation: { required: true }, admin: { readOnly: true, hideFromCollection: true } },
        room: { name: "Room", type: "string", admin: { readOnly: true } },
        at: { name: "When", type: "date", autoValue: "on_create", validation: { required: true }, admin: { readOnly: true } },
    },
    indexes: [
        { on: ["gameKey", "player"], unique: true, reason: "a phone's result is stored once per game (POST /games/me, on conflict do nothing)" },
        { on: ["player"], reason: "a player's games, and the players rebuild counts them" },
        { on: ["gameId"], reason: "a game's players" },
    ],
    admin: {
        icon: "Medal",
        group: "System",
        openEntityMode: "side_panel",
        sort: ["at", "desc"],
        display: {
            title: ({ entity }) => entity.values.name || "No name",
            subtitle: ({ entity }) => [entity.values.pos ? `No. ${entity.values.pos} of ${entity.values.ofPlayers ?? "?"}` : "", moment(entity.values.at), entity.values.room && `room ${entity.values.room}`].filter(Boolean).join(" · ") || undefined,
            date: "at",
        },
        listProperties: ["pos", "score", "rightAnswers"],
        form: {
            sections: [
                { key: "result", properties: ["who", "name", "pos", "ofPlayers", "score", "rightAnswers", "game", "room", "at"] },
                { key: "ids", title: "Behind the scenes", collapsed: true, properties: ["id", "player", "gameId", "gameKey"] },
            ],
        },
    },
});

export default gamePlayersCollection;
